import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Heart, Twitter, Facebook, Linkedin, Share2, X, Instagram, MessageCircle, Copy, Link } from 'lucide-react';
import { copyToClipboard, shareNative, shareToPlatform } from '../utils/share';

interface ShareButtonProps {
  title?: string;
  text?: string;
  url?: string;
  label?: string;
  contentLabel?: string;
  theme?: 'light' | 'dark';
  onShare?: (method: string) => void;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  title = "BirthVerse - Find Your Birthday Bible Verse",
  text = "Discover your personal Bible verse based on your birthday!",
  url = window.location.href,
  label = "Share God's Love",
  contentLabel = 'verse',
  theme = 'light',
  onShare
}) => {
  const [showPlatforms, setShowPlatforms] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [position, setPosition] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const [showInstructions, setShowInstructions] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const shareButton = useRef<HTMLButtonElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const options = useRef<HTMLDivElement>(null);
  const optionsId = useId();
  const nativeAvailable = typeof navigator.share === 'function' && window.isSecureContext;
  const TriggerIcon = label === "Share God's Love" ? Heart : Share2;
  const subject = contentLabel.charAt(0).toUpperCase() + contentLabel.slice(1);

  const platforms = [
    { key: 'twitter', name: 'Twitter', icon: Twitter },
    { key: 'facebook', name: 'Facebook', icon: Facebook },
    { key: 'linkedin', name: 'LinkedIn', icon: Linkedin },
    { key: 'reddit', name: 'Reddit', icon: MessageCircle },
    { key: 'instagram', name: 'Instagram', icon: Instagram },
    { key: 'tiktok', name: 'TikTok', icon: Share2 }
  ];

  useEffect(() => {
    const updateViewport = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useLayoutEffect(() => {
    if (!showPlatforms || isMobile || !options.current) return;
    const panel = options.current;
    const updatePosition = () => {
      if (!shareButton.current || window.innerWidth < 640) return;
      const anchor = shareButton.current.getBoundingClientRect();
      const margin = 12;
      const gap = 8;
      const width = Math.min(280, window.innerWidth - margin * 2);
      const maxHeight = Math.max(0, window.innerHeight - margin * 2);
      const height = Math.min(panel.scrollHeight + 2, maxHeight);
      const fitsBelow = window.innerHeight - margin - anchor.bottom - gap >= height;
      const preferredTop = fitsBelow ? anchor.bottom + gap : anchor.top - gap - height;
      const next = {
        top: Math.max(margin, Math.min(preferredTop, window.innerHeight - margin - height)),
        left: Math.max(margin, Math.min(anchor.right - width, window.innerWidth - margin - width)),
        width,
        maxHeight
      };
      setPosition(previous => previous && Object.keys(next).every(key => previous[key as keyof typeof next] === next[key as keyof typeof next]) ? previous : next);
    };
    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(panel);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showPlatforms, isMobile, showInstructions, status, error, nativeAvailable]);

  useEffect(() => {
    if (!showPlatforms) return;
    options.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !container.current?.contains(event.target) && !options.current?.contains(event.target)) {
        setShowPlatforms(false);
        setShowInstructions(null);
      }
    };
    document.addEventListener('pointerdown', dismissOutside);
    return () => document.removeEventListener('pointerdown', dismissOutside);
  }, [showPlatforms, showInstructions]);

  const closeOptions = () => {
    setShowPlatforms(false);
    setShowInstructions(null);
    shareButton.current?.focus();
  };

  const openOptions = () => {
    setError('');
    setStatus('');
    setShowInstructions(null);
    setShowPlatforms(true);
  };

  const copyLink = async (instructionsFor?: string) => {
    setPending(true);
    setError('');
    setStatus('');
    try {
      await copyToClipboard(url);
      setStatus('Result link copied.');
      onShare?.('copy_link');
      if (instructionsFor) setShowInstructions(instructionsFor);
      else closeOptions();
    } catch {
      setError('Could not copy the link. Try again, or choose another sharing option.');
    } finally {
      setPending(false);
    }
  };

  const copyShareText = async () => {
    setPending(true);
    setError('');
    setStatus('');
    try {
      const shareText = text.includes(url) ? text : [text, url].filter(Boolean).join('\n');
      await copyToClipboard(shareText);
      setStatus('Share text copied.');
      onShare?.('clipboard');
      closeOptions();
    } catch {
      setError('Could not copy the share text. Try again, or choose another sharing option.');
    } finally {
      setPending(false);
    }
  };

  const shareToSocialMedia = async (platform: typeof platforms[number]) => {
    if (platform.key === 'instagram' || platform.key === 'tiktok') {
      await copyLink(platform.name);
      return;
    }

    setError('');
    setStatus('');
    try {
      if (!shareToPlatform(platform.key, { title, text, url })) {
        setError('The sharing window was blocked. Allow popups and try again, or copy the link.');
        return;
      }
      onShare?.(platform.key);
      setStatus('Sharing window opened. Finish your post there.');
      closeOptions();
    } catch {
      setError('Could not open the sharing window. Try again, or copy the link.');
    }
  };

  const shareOnDevice = async () => {
    setPending(true);
    setError('');
    setStatus('');
    try {
      const result = await shareNative({ title, text, url });
      if (result === 'shared') {
        setStatus(`${subject} shared.`);
        onShare?.('native');
        closeOptions();
      } else if (result === 'unavailable') {
        setStatus('Device sharing is unavailable. Choose another sharing option.');
      } else {
        setStatus('Sharing cancelled.');
      }
    } catch {
      setError(`Could not share the ${contentLabel}. Try again, or choose another sharing option.`);
    } finally {
      setPending(false);
    }
  };

  const feedback = (
    <>
      {status && <p role="status" className={`mt-2 text-sm ${showPlatforms || theme === 'light' ? 'text-gray-600' : 'text-slate-200'}`}>{status}</p>}
      {error && <p role="alert" className={`mt-2 text-sm ${showPlatforms || theme === 'light' ? 'text-red-700' : 'text-red-300'}`}>{error}</p>}
    </>
  );

  const sharingOptions = showPlatforms ? (
    <div
      ref={options}
      id={optionsId}
      role="dialog"
      aria-label="Sharing options"
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeOptions();
        } else if (event.key === 'Tab') {
          const buttons = Array.from(options.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') || []);
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      }}
      style={isMobile ? undefined : position || undefined}
      className="fixed inset-x-3 bottom-3 z-50 max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white text-gray-700 rounded-lg shadow-xl border border-gray-200 p-2 sm:inset-x-auto sm:bottom-auto sm:w-[280px]"
    >
      <div className="flex justify-between items-center px-3 py-2 border-b border-gray-100">
        <span className="text-sm font-medium text-gray-700">Share via</span>
        <button type="button" onClick={closeOptions} aria-label="Close sharing options" className="p-2 text-gray-500 hover:text-gray-700 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4F46E5]">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {showInstructions ? (
        <div className="p-4">
          <h3 className="font-medium text-gray-900 mb-3">Share on {showInstructions}</h3>
          <ol className="space-y-2 text-sm text-gray-600 list-decimal pl-4">
            <li>Your result link is copied.</li>
            <li>Open {showInstructions} and create a post or story.</li>
            <li>Paste the link where links are supported and add your {contentLabel} if you wish.</li>
          </ol>
          <button type="button" onClick={() => setShowInstructions(null)} className="mt-4 w-full px-4 py-2 text-sm text-[#4F46E5] hover:bg-[#F5F3FF] rounded-md">
            Back to sharing options
          </button>
        </div>
      ) : (
        <div className="p-1">
          {nativeAvailable && (
            <button type="button" disabled={pending} onClick={shareOnDevice} className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 rounded-md disabled:opacity-60">
              <Share2 className="h-4 w-4" aria-hidden="true" />
              <span>Share on your device</span>
            </button>
          )}
          {platforms.map(platform => (
            <button key={platform.key} type="button" disabled={pending} onClick={() => shareToSocialMedia(platform)} className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 rounded-md disabled:opacity-60">
              <platform.icon className="h-4 w-4" aria-hidden="true" />
              <span>Share on {platform.name}</span>
            </button>
          ))}
          <div className="border-t border-gray-100 my-1" />
          <button type="button" disabled={pending} onClick={copyShareText} className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 rounded-md disabled:opacity-60">
            <Copy className="h-4 w-4" aria-hidden="true" />
            <span>Copy share text</span>
          </button>
          <button type="button" disabled={pending} onClick={() => copyLink()} className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 rounded-md disabled:opacity-60">
            <Link className="h-4 w-4" aria-hidden="true" />
            <span>Copy link</span>
          </button>
        </div>
      )}
      <div className="px-3">{feedback}</div>
    </div>
  ) : null;
  return (
    <div ref={container} className="relative">
      <button
        ref={shareButton}
        type="button"
        onClick={() => showPlatforms ? closeOptions() : openOptions()}
        className={`flex items-center gap-2 px-3 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-600 text-white hover:bg-indigo-500 focus:ring-offset-slate-900' : 'text-[#4F46E5] hover:text-[#4338CA]'}`}
        aria-label={label}
        aria-haspopup="dialog"
        aria-controls={showPlatforms ? optionsId : undefined}
        aria-expanded={showPlatforms}
      >
        <TriggerIcon className="h-5 w-5" aria-hidden="true" />
        <span className="font-medium">{label}</span>
      </button>

      {sharingOptions && createPortal(sharingOptions, document.body)}
      {!showPlatforms && <div className="max-w-[17rem]">{feedback}</div>}
    </div>
  );
};

export default ShareButton;
